import React, { useContext, useState } from 'react';
import Grid from '@mui/material/Grid2';
import { useEffect } from 'react';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CancelIcon from '@mui/icons-material/Cancel';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SessionContext } from '../../Contexts/SessionContext';
import { APIContext } from "../../Contexts/APIContext";
import { SiteNavigationType } from "../../Utilities/ViewModels/SiteNavigationType";
import { SessionState } from '../../Contexts/SessionState';
import ExportSiteListDialog from "./ExportSiteListDialog";
import ExportSiteListViewModel from "../ViewModels/ExportSiteListViewModel";
import SiteFilterViewModel from "../../TripRate/ViewModels/SiteFilterViewModel";
import AdditionalInfo from '../../Popups/AdditionalInfo';
import SiteMapDialog from '../../Site/SiteMap/SiteMap';
import { SurveyFullViewModel } from '../../TripRateNew/ViewModels/SurveyFullViewModel';
import { useLocation, useNavigate } from 'react-router-dom';

interface IProps {
    getData: (newState?: SessionState) => void;
    type?: SiteNavigationType;
    filter?: SiteFilterViewModel;
    mapList?: SurveyFullViewModel[];
}

export default function SiteListTextSearchAndLegend({ getData, type, filter, mapList }: IProps) {
    const { sessionState, updateSessionState } = useContext(SessionContext);
    const { postPDF, postCSV } = useContext(APIContext);
    const location = useLocation();
    const navigate = useNavigate();
    const [exportDialog, setExportDialog] = useState(false);
    const [mapOpen, setMapOpen] = useState(false);
    const searchParams = new URLSearchParams(location.search);
    const origin = searchParams.get('origin');

    useEffect(() => {
        setMapOpen(type === 'siteList' && origin === 'map');
    }, [origin, type]);

    const openMap = () => {
        const nextParams = new URLSearchParams(location.search);
        nextParams.set('origin', 'map');
        navigate({ pathname: location.pathname, search: nextParams.toString() }, { replace: true });
        setMapOpen(true);
    };

    const closeMap = () => {
        const nextParams = new URLSearchParams(location.search);
        nextParams.delete('origin');
        navigate({ pathname: location.pathname, search: nextParams.toString() }, { replace: true });
        setMapOpen(false);
    };

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const nextState = {
            ...sessionState.siteListTableState,
            [type === 'summaryFiltering' ? 'summaryFiltering' : 'siteList']: {
                ...sessionState.siteListTableState[type === 'summaryFiltering' ? 'summaryFiltering' : 'siteList'],
                page: 0,
            },
        };
        updateSessionState({
            siteListPage: 0,
            siteListTextSearch: event.target.value,
            siteListTableState: nextState
        });
    };

    const runSearch = () => {
        getData();
    };

    const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            runSearch();
        }
    };

    const clearSearch = () => {
        const nextState = {
            ...sessionState.siteListTableState,
            [type === 'summaryFiltering' ? 'summaryFiltering' : 'siteList']: {
                ...sessionState.siteListTableState[type === 'summaryFiltering' ? 'summaryFiltering' : 'siteList'],
                page: 0,
            },
        };
        updateSessionState(
            { siteListPage: 0, siteListTextSearch: '', siteListTableState: nextState },
            (state: SessionState | undefined) => {
                getData(state);
            }
        );
    };

    const getReport = async (exportFilters: ExportSiteListViewModel) => {
        exportFilters.filter = filter ?? new SiteFilterViewModel();
        if (type === "summaryFiltering") {
            exportFilters.filter.references = filter?.references ?? sessionState.selectedSitesAndSurveys
                .filter(x => x.siteActive && x.surveyActive)
                .map(x => x.surveyRef);

            const removedSurveys = sessionState.selectedSitesAndSurveys
                .filter(x => !x.siteActive || !x.surveyActive)
                .map(x => ({
                    reference: x.surveyRef,
                    reason: x.reasonForSiteRemoval ?? x.reasonForSurveyRemoval ?? "Unknown reason"
                }));

            exportFilters.filter.removedSurveys = removedSurveys;
        }
        exportFilters.mainUse = type == "siteList" ? sessionState.siteListMain : sessionState.tripRateMain;
        exportFilters.subUse = type == "siteList" ? sessionState.siteListSub : sessionState.tripRateSub
        exportFilters.order = sessionState.siteListDir;
        exportFilters.orderBy = sessionState.siteListSort;
        exportFilters.headers = sessionState.siteListHeaders;
        exportFilters.type == "PDF"
            ? postPDF(
                "PDFGenerator",
                `GetSiteListPDF`,
                exportFilters.fileName,
                exportFilters.displayToScreen,
                exportFilters
            )
            : postCSV(
                "CsvGenerator",
                `GetSiteListCSV`,
                exportFilters.fileName,
                exportFilters
            );
    };

    return (
        <>
            <Grid container>
                <Grid size={(type === "siteList" || type === "summaryFiltering") ? 8 : 10}>
                    <TextField
                        size="small"
                        onChange={handleSearch}
                        onKeyDown={handleSearchKeyDown}
                        value={sessionState.siteListTextSearch}
                        placeholder="Search..."
                        fullWidth
                        InputProps={{
                            endAdornment: sessionState.siteListTextSearch && (
                                <IconButton
                                    size="small"
                                    aria-label="clear search"
                                    onClick={clearSearch}
                                >
                                    <CancelIcon fontSize="small" />
                                </IconButton>
                            )
                        }}
                    />
                </Grid>

                <Grid size={2}>
                    <Button
                        sx={{ verticalAlign: 'text-bottom' }}
                        onClick={runSearch}
                        variant="contained"
                    >
                        Search
                    </Button>
                </Grid>

                {(type === "siteList" || type === "summaryFiltering") && (
                    <>
                        <Grid size={2}>
                            <Button
                                sx={{ verticalAlign: 'text-bottom' }}
                                onClick={() => setExportDialog(true)}
                                variant="contained"
                            >
                                Export
                            </Button>
                        </Grid>



                        <Grid size={12}>
                            <Grid
                                container
                                alignItems="center"
                                spacing={1}
                                justifyContent={type === 'siteList' ? 'flex-start' : 'flex-end'}
                            >
                                {type == "siteList" && <Grid>
                                    <Button
                                        sx={{ verticalAlign: 'text-bottom' }}
                                        variant="outlined"
                                        onClick={openMap}
                                    >
                                        Map
                                    </Button>
                                </Grid>}
                                <Grid>
                                    <Box
                                        style={{
                                            backgroundColor: "rgb(192, 220, 192)",
                                            width: 15,
                                            height: 33,
                                            border: "4px solid white",
                                            borderRadius: "9px"
                                        }}
                                    />
                                </Grid>
                                <Grid>
                                    <Typography variant="body1">
                                        COVID-19 Restrictions
                                    </Typography>
                                </Grid>
                                <Grid>
                                    <AdditionalInfo
                                        type="dialog"
                                        colour="info"
                                        icon="help"
                                        dialogTitle="COVID-19 Restrictions"
                                        dialogContent='This survey was undertaken at a time of Covid-19 (coronavirus) restrictions. Therefore, it is possible that trip levels and patterns at such sites at the time of their surveys may have differed from what would have been considered "normal" pre-pandemic, although to what scale there may be differences (if any) will vary from site to site. Users should note that no surveys were undertaken during full lockdowns (as per the UK lockdown that commenced in March 2020), and the levels of restrictions would have differed at different times and within different regions throughout the pandemic. TRICS cannot provide guidance on whether sites undertaken during restrictions should be included or excluded in sets of data for trip generation. We continued to undertake surveys through the pandemic when full lockdowns did not apply, as it is essential that we continually add surveys to the database moving forwards. So our users are encouraged to apply their own professional judgement in terms of site inclusion, and in the case of site exclusion should always provide their own reasons within their reports. And, as always, we encourage our users to follow the TRICS Good Practice Guide.'
                                    />
                                </Grid>
                                <Grid>
                                    <Box
                                        style={{
                                            backgroundColor: "rgb(255, 215, 0)",
                                            width: 15,
                                            height: 33,
                                            border: "4px solid white",
                                            borderRadius: "9px"
                                        }}
                                    />
                                </Grid>
                                <Grid>
                                    <Typography variant="body1">
                                        Standard Assessment Methodology Survey
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    </>
                )}
            </Grid>

            {/* ✅ Export dialog */}
            <ExportSiteListDialog
                handleClose={() => setExportDialog(false)}
                dialogOpen={exportDialog}
                exportSiteList={getReport}
            />

            {type === "siteList" && mapList &&
                <SiteMapDialog
                    open={mapOpen}
                    onClose={closeMap}
                    sites={mapList}
                    navigationType="siteList"
                    fromTab="siteSummary"
                    returnLabel="Back to Map"
                />
            }
        </>
    );
}
